// Development utility to populate sample data
// This file is only for development purposes

import { dbOperations, creditCardOperations } from '../db/database';

const sampleEntries = [
  // Income entries - October 2024
  {
    category: 'Income' as const,
    title: 'Monthly Salary',
    value: 5000,
    date: new Date(2024, 9, 1),
    type: 'income' as const,
    isRecurring: true,
    recurringFrequency: 'monthly' as const,
  },
  {
    category: 'Income' as const,
    title: 'Freelance Project',
    value: 1200,
    date: new Date(2024, 9, 15),
    type: 'income' as const,
  },
  {
    category: 'Income' as const,
    title: 'Bonus',
    value: 500,
    date: new Date(2024, 9, 20),
    type: 'income' as const,
  },

  // Expenses - October 2024
  {
    category: 'Rent/Mortgage' as const,
    title: 'Monthly Rent',
    value: 1500,
    date: new Date(2024, 9, 1),
    type: 'expense' as const,
    isRecurring: true,
    recurringFrequency: 'monthly' as const,
  },
  {
    category: 'Subscription' as const,
    title: 'Netflix',
    value: 15.99,
    date: new Date(2024, 9, 5),
    type: 'expense' as const,
    isRecurring: true,
    recurringFrequency: 'monthly' as const,
  },
  {
    category: 'Subscription' as const,
    title: 'Spotify',
    value: 9.99,
    date: new Date(2024, 9, 5),
    type: 'expense' as const,
    isRecurring: true,
    recurringFrequency: 'monthly' as const,
  },
  {
    category: 'Subscription' as const,
    title: 'Adobe Creative Cloud',
    value: 54.99,
    date: new Date(2024, 9, 7),
    type: 'expense' as const,
    isRecurring: true,
    recurringFrequency: 'monthly' as const,
  },
  {
    category: 'Other' as const,
    title: 'Grocery Shopping',
    value: 120.50,
    date: new Date(2024, 9, 10),
    type: 'expense' as const,
  },
  {
    category: 'Vehicle' as const,
    title: 'Gas',
    value: 45.00,
    date: new Date(2024, 9, 12),
    type: 'expense' as const,
  },
  {
    category: 'Other' as const,
    title: 'Coffee Shop',
    value: 12.50,
    date: new Date(2024, 9, 14),
    type: 'expense' as const,
  },
  {
    category: 'Credit Card' as const,
    title: 'Credit Card Payment',
    value: 250.00,
    date: new Date(2024, 9, 15),
    type: 'expense' as const,
  },
  {
    category: 'Other' as const,
    title: 'Restaurant Dinner',
    value: 85.00,
    date: new Date(2024, 9, 16),
    type: 'expense' as const,
  },
  {
    category: 'Vehicle' as const,
    title: 'Car Insurance',
    value: 120.00,
    date: new Date(2024, 9, 18),
    type: 'expense' as const,
  },
  {
    category: 'Other' as const,
    title: 'Grocery Shopping',
    value: 95.30,
    date: new Date(2024, 9, 20),
    type: 'expense' as const,
  },
  {
    category: 'Property' as const,
    title: 'Home Repairs',
    value: 300.00,
    date: new Date(2024, 9, 22),
    type: 'expense' as const,
  },
  {
    category: 'Other' as const,
    title: 'Movie Tickets',
    value: 25.00,
    date: new Date(2024, 9, 23),
    type: 'expense' as const,
  },
  {
    category: 'Loan' as const,
    title: 'Student Loan Payment',
    value: 200.00,
    date: new Date(2024, 9, 25),
    type: 'expense' as const,
  },

  // Previous month (September 2024)
  {
    category: 'Income' as const,
    title: 'Monthly Salary',
    value: 5000,
    date: new Date(2024, 8, 1),
    type: 'income' as const,
  },
  {
    category: 'Rent/Mortgage' as const,
    title: 'Monthly Rent',
    value: 1500,
    date: new Date(2024, 8, 1),
    type: 'expense' as const,
  },
  {
    category: 'Other' as const,
    title: 'Grocery Shopping',
    value: 110.00,
    date: new Date(2024, 8, 15),
    type: 'expense' as const,
  },
];

const sampleCreditCards = [
  {
    name: 'Visa Platinum',
    balance: 2500,
    interestRate: 18.9,
    minimumPayment: 75,
  },
  {
    name: 'Mastercard Gold',
    balance: 1200,
    interestRate: 21.5,
    minimumPayment: 40,
  },
  {
    name: 'Store Card',
    balance: 450,
    interestRate: 29.9,
  },
];

export async function populateSampleData(): Promise<number> {
  try {
    console.log('🚀 Populating sample data...');
    
    // Add budget entries
    for (const entry of sampleEntries) {
      await dbOperations.addEntry(entry);
      console.log(`✅ Added: ${entry.title} - £${entry.value}`);
    }
    
    // Add credit cards
    for (const card of sampleCreditCards) {
      await creditCardOperations.addCard(card);
      console.log(`✅ Added card: ${card.name} - £${card.balance}`);
    }
    
    console.log(`✅ Successfully added ${sampleEntries.length} entries and ${sampleCreditCards.length} credit cards!`);
    return sampleEntries.length + sampleCreditCards.length;
  } catch (error) {
    console.error('❌ Error populating sample data:', error);
    throw error;
  }
}

// Make it available in the browser console for development
if (process.env.NODE_ENV === 'development') {
  (window as any).populateSampleData = populateSampleData;
  (window as any).clearAllData = async () => {
    await dbOperations.clearAllEntries();
    await creditCardOperations.clearAllCards();
    console.log('✅ All data cleared!');
  };
  
  console.log('💡 Development helpers available:');
  console.log('   - window.populateSampleData() - Add sample entries & credit cards');
  console.log('   - window.clearAllData() - Clear all entries & credit cards');
}
